import { Platform, Text, View, type ViewStyle } from "react-native";

import { COLORS, FONTS } from "../../lib/brand";
import { PANE_THEMES } from "../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;
import { useEffect, useRef, useState, type ReactNode } from "react";


/**
 * Web-only drop target. On native this is a transparent passthrough — the
 * native file picker handles uploads. On web, dragging a file over the card
 * lights up a dashed amber overlay; dropping calls `onFile` with the file.
 *
 * Wrap a Card or other content; the overlay absolutely-fills the wrapper
 * when a drag is active.
 */
export function DeliverableDropZone({
  enabled,
  onFile,
  children,
  fileTypeHint,
}: {
  enabled: boolean;
  onFile: (file: File) => void;
  children: ReactNode;
  /** e.g. "PDF only" — rendered in the overlay so the user knows what's accepted. */
  fileTypeHint?: string;
}) {
  const [hover, setHover] = useState(false);
  const ref = useRef<View | null>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || !enabled) return;
    const node = ref.current as unknown as HTMLElement | null;
    if (!node) return;

    let depth = 0;
    function onEnter(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth++;
      setHover(true);
    }
    function onOver(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    }
    function onLeave(_e: DragEvent) {
      depth = Math.max(0, depth - 1);
      if (depth === 0) setHover(false);
    }
    function onDrop(e: DragEvent) {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setHover(false);
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        onFile(files[0]);
      }
    }

    node.addEventListener("dragenter", onEnter as unknown as EventListener);
    node.addEventListener("dragover", onOver as unknown as EventListener);
    node.addEventListener("dragleave", onLeave as unknown as EventListener);
    node.addEventListener("drop", onDrop as unknown as EventListener);

    return () => {
      node.removeEventListener("dragenter", onEnter as unknown as EventListener);
      node.removeEventListener("dragover", onOver as unknown as EventListener);
      node.removeEventListener("dragleave", onLeave as unknown as EventListener);
      node.removeEventListener("drop", onDrop as unknown as EventListener);
    };
  }, [enabled, onFile]);

  if (Platform.OS !== "web" || !enabled) {
    return <View ref={ref}>{children}</View>;
  }

  return (
    <View ref={ref} style={{ position: "relative" }}>
      {children}
      {hover ? (
        <View
          pointerEvents="none"
          style={
            {
              position: "absolute",
              inset: 0,
              borderRadius: 8,
              borderWidth: 2,
              borderStyle: "dashed",
              borderColor: COLORS.AMBER.hex,
              backgroundColor: STUDENT_THEME.accentSoft,
              alignItems: "center",
              justifyContent: "center",
              padding: 16,
            } as unknown as ViewStyle
          }
        >
          <View
            style={{
              backgroundColor: COLORS.FOREST.hex,
              paddingHorizontal: 18,
              paddingVertical: 10,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: "rgba(181,135,58,0.50)",
              alignItems: "center",
            }}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: COLORS.AMBER.hex,
              }}
            >
              Drop to upload
            </Text>
            {fileTypeHint ? (
              <Text
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 11,
                  color: "rgba(247,244,238,0.65)",
                  marginTop: 2,
                }}
              >
                {fileTypeHint}
              </Text>
            ) : null}
          </View>
        </View>
      ) : null}
    </View>
  );
}

function hasFiles(e: DragEvent): boolean {
  const dt = e.dataTransfer;
  if (!dt) return false;
  if (dt.types && Array.from(dt.types).some((t) => t === "Files")) return true;
  return (dt.files?.length ?? 0) > 0;
}
